/* eslint-disable no-mixed-spaces-and-tabs */
import React, { useMemo } from 'react';
import { useObjectContext } from '@/hooks/useObjectContext';
import { CreateNoteButton } from '@/components/buttons/viewHeaderButtons/CreateNoteButton';
import { CreateTaskButton } from '@/components/buttons/viewHeaderButtons/CreateTaskButton';
import { TabView } from '@/components/layouts/TabView';
import { getDealTabs } from 'app/tabs';
import { GenerateRiskButton } from '@/components/buttons/AddRiskInfoButton';
import { AddQuoteButton } from '@/components/buttons/AddQuoteButton';
import { SendScopeOfAdviceButton } from '@/components/buttons/viewHeaderButtons/SendScopeOfAdviceButton';
import { getPipelineIcon } from '@/utils/constants';
import { PreRenewalEmailButton } from '@/components/buttons/PreRenewalEmailButton';
import { AddPolicyButton } from '@/components/buttons/viewHeaderButtons/AddPolicyButton';
import { useFeatureFlagContext } from '@/context/featureFlagContext';
import { AI_EMAIL_WORKFLOW_TYPES } from '@/features/aiWorkflow/constants';
import GenerateAITemplateMenuButton from '@/components/buttons/GenerateAITemplateMenuButton';
import { Stack, useTheme } from '@mui/material';
import PortfolioStatementBanner from '@/components/layouts/PortfolioStatementBanner';
import EngageReportsBanner from '@/components/layouts/EngageReportsBanner';
import FolioEngageShareLinkButton from '@/components/buttons/FolioEngageShareLinkButton';

const RenewalHeaderComponent = () => {
	const { getFeatureFlags } = useFeatureFlagContext();
	const showDealTabEmailButtons = Boolean(getFeatureFlags('showDealTabEmailButtons')?.active);

	return (
		<>
			<AddPolicyButton />
			{showDealTabEmailButtons && (
				<>
					<PreRenewalEmailButton />
				</>
			)}
		</>
	);
};

const DealHeaderComponent = () => {
	const { getFeatureFlags } = useFeatureFlagContext();
	const showDealTabEmailButtons = Boolean(getFeatureFlags('showDealTabEmailButtons')?.active);

	return (
		<>
			<AddPolicyButton />
			{showDealTabEmailButtons && (
				<>
					<SendScopeOfAdviceButton />
				</>
			)}
		</>
	);
};

const RiskInfoHeaderComponent = () => {
	const { dealRow } = useObjectContext();
	const isRenewal = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_RENEWALS;
	const { getFeatureFlags } = useFeatureFlagContext();
	const isFolioEngageEnabled = Boolean(getFeatureFlags('folioEngage')?.active);

	const workflowTypes = [
		AI_EMAIL_WORKFLOW_TYPES.riskInfoNBEmail,
		...(isRenewal
			? [
					AI_EMAIL_WORKFLOW_TYPES.preRenewalBasic,
					AI_EMAIL_WORKFLOW_TYPES.riskInfoRenewalEmail,
				]
			: []),
	];

	const engageReportTypes = isFolioEngageEnabled && isRenewal ? ['pre_renewal_report'] : [];

	const items = [
		...workflowTypes.map((type) => ({ type, workflowType: 'aiWorkflow' })),
		...engageReportTypes.map((type) => ({ type, workflowType: 'folioEngage' })),
	];

	return (
		<Stack width='100%' spacing={1}>
			{isRenewal && <PortfolioStatementBanner />}
			{engageReportTypes.length > 0 && (
				<EngageReportsBanner reportTypes={engageReportTypes} />
			)}
			<Stack spacing={1} direction='row' justifyContent={'flex-end'} alignItems={'center'}>
				{engageReportTypes.length > 0 && (
					<FolioEngageShareLinkButton reportTypes={engageReportTypes} />
				)}
				<GenerateRiskButton />
				<GenerateAITemplateMenuButton {...{ items }} />
			</Stack>
		</Stack>
	);
};

const QuotesHeaderComponent = () => {
	const { dealRow } = useObjectContext();
	const isRenewal = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_RENEWALS;
	const isNewBusiness = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_NEW_BUSINESS;
	const { getFeatureFlags } = useFeatureFlagContext();
	const isFolioEngageEnabled = Boolean(getFeatureFlags('folioEngage')?.active);

	const workflowTypes = [
		isRenewal
			? AI_EMAIL_WORKFLOW_TYPES.renewalTermsEmail
			: AI_EMAIL_WORKFLOW_TYPES.quoteRecommendationEmail,
	];

	const engageReportTypes = !isFolioEngageEnabled
		? []
		: isRenewal
			? ['renewal_terms_report']
			: isNewBusiness
				? ['new_business_proposal']
				: [];

	const items = [
		...workflowTypes.map((type) => ({ type, workflowType: 'aiWorkflow' })),
		...engageReportTypes.map((type) => ({ type, workflowType: 'folioEngage' })),
	];

	return (
		<Stack width='100%' spacing={1}>
			{engageReportTypes.length > 0 && (
				<EngageReportsBanner reportTypes={engageReportTypes} />
			)}
			<Stack spacing={1} direction='row' justifyContent={'flex-end'} alignItems={'center'}>
				{engageReportTypes.length > 0 && (
					<FolioEngageShareLinkButton reportTypes={engageReportTypes} />
				)}
				<AddQuoteButton />
				<GenerateAITemplateMenuButton {...{ items }} />
			</Stack>
		</Stack>
	);
};

export const DealTabView = () => {
	const theme = useTheme();

	const { dealRow } = useObjectContext();

	const pipelineId = dealRow?.pipeline;

	const isRenewal = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_RENEWALS;
	const isClaim = dealRow?.pipeline == process.env.REACT_APP_PIPELINE_CLAIMS;

	const headerComponents = useMemo(
		() => ({
			...(!isClaim && {
				'': isRenewal ? RenewalHeaderComponent : DealHeaderComponent,
			}),
			notes: CreateNoteButton,
			tasks: CreateTaskButton,
			risk: RiskInfoHeaderComponent,
			quotes: QuotesHeaderComponent,
		}),
		[isRenewal, isClaim],
	);

	return (
		<TabView
			objectType='deal'
			getTabs={getDealTabs}
			headerComponents={headerComponents}
			icon={getPipelineIcon(
				pipelineId,
				'55px',
				theme.palette.mode === 'dark' ? { bgColor: 'transparent' } : undefined,
			)}
		/>
	);
};
DealTabView.displayName = 'DealTabView';
